import type {
  LiveReadyDraftIntake,
  ScenarioOperatingMode,
  WorkspaceWorkflowScenario,
} from "@vekha/contracts";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { updateScenarioOperatingMode } from "./api";
import { useApp } from "./app-context";
import { useNativeCalendar } from "./native-calendar-context";

export const ALL_ACTIVE_SCENARIOS = "all_active";
export const ALL_ACTIVE_AGENTS = "all_active_agents";

type WorkContextValue = Readonly<{
  agentId: string;
  scenarioId: string;
  agents: ReturnType<typeof useApp>["data"]["employees"];
  activeScenarios: WorkspaceWorkflowScenario[];
  selectedScenario: WorkspaceWorkflowScenario | null;
  setAgentId: (agentId: string) => void;
  setScenarioId: (scenarioId: string) => void;
  setMode: (mode: "off" | "drafts" | "live") => Promise<void>;
  modePending: boolean;
  modeError: string | null;
  modeNotice: string | null;
  contextNotice: string | null;
  contextSearch: string;
}>;

const WorkContext = createContext<WorkContextValue | null>(null);

function activeForAgent(
  scenarios: readonly WorkspaceWorkflowScenario[],
  agentId: string,
): WorkspaceWorkflowScenario[] {
  if (agentId === ALL_ACTIVE_AGENTS) {
    return scenarios.filter((scenario) => scenario.activeActivation !== null && scenario.latestVersion !== null);
  }
  return scenarios.filter((scenario) => (
    scenario.primaryEmployeeId === agentId
    && scenario.activeActivation !== null
    && scenario.latestVersion !== null
  ));
}

export function resolveWorkContext(
  agents: readonly Readonly<{ id: string; name: string }>[],
  scenarios: readonly WorkspaceWorkflowScenario[],
  requestedAgentId: string,
  requestedScenarioId: string,
): Readonly<{
  agentId: string;
  scenarioId: string;
  activeScenarios: WorkspaceWorkflowScenario[];
  notice: string | null;
}> {
  const requestedAgentAvailable = requestedAgentId === ALL_ACTIVE_AGENTS
    || agents.some((agent) => agent.id === requestedAgentId);
  const agentId = requestedAgentAvailable
    ? requestedAgentId
    : agents.find((agent) => agent.name === "Яна")?.id ?? agents[0]?.id ?? "";
  const agent = agents.find((item) => item.id === agentId);
  const activeScenarios = activeForAgent(scenarios, agentId);
  const requestedScenarioAvailable = requestedScenarioId === ALL_ACTIVE_SCENARIOS
    || activeScenarios.some((scenario) => scenario.id === requestedScenarioId);
  const scenarioId = requestedScenarioAvailable
    ? requestedScenarioId
    : activeScenarios[0]?.id ?? ALL_ACTIVE_SCENARIOS;
  // No agents yet: the snapshot has not come, or the page reads without it («Сайты»). Nothing was dropped then, and
  // the notice would stay up after the agents arrive (Ден 18.10.2026: «агент «доступный»» on the marketing pages).
  const notice = !agents.length ? null
    : requestedAgentId && !requestedAgentAvailable
    ? `Ранее выбранный агент больше недоступен. Показан агент «${agent?.name ?? "доступный"}».`
    : requestedScenarioId && !requestedScenarioAvailable && scenarios.length
      ? `Ранее выбранный сценарий больше не активен. Показан первый активный сценарий ${agent ? `агента «${agent.name}»` : "из доступных"}.`
      : null;
  return { agentId, scenarioId, activeScenarios, notice };
}

/** The menu's links carry the work context. Without agents — a page with a label in its top bar reads none of the snapshot
 * (18.10.2026), or the context has not come yet — they carry what the address asked, and «Диалоги» resolve it as they do on
 * their own (Яна and her first active scenario), not «all scenarios» of nobody. */
export function contextSearchOf(
  agentsKnown: boolean,
  agentId: string,
  scenarioId: string,
  requestedAgentId: string,
  requestedScenarioId: string,
): string {
  const search = new URLSearchParams();
  if (!agentsKnown) {
    if (requestedAgentId) search.set("agent", requestedAgentId);
    if (requestedScenarioId) search.set("scenario", requestedScenarioId);
    const asked = search.toString();
    return asked ? `?${asked}` : "";
  }
  if (agentId) search.set("agent", agentId);
  search.set("scenario", scenarioId);
  return `?${search.toString()}`;
}

export function workModeLabel(mode: ScenarioOperatingMode): string {
  if (mode === "live") return "Автопилот";
  if (mode === "drafts" || mode === "preview") return "Черновики";
  return "Выключен";
}

export function liveReadyDraftIntakeNotice(intake: LiveReadyDraftIntake): string | null {
  if (intake.claimed === 0) return null;
  if (intake.queued === 0 && intake.skipped > 0) {
    return `Автопилот включён. ${intake.skipped} готовых пакетов пропущены: они уже не актуальны.`;
  }
  if (intake.skipped === 0) {
    return `Автопилот подхватил ${intake.queued} готовых пакетов без новой генерации.`;
  }
  return `Автопилот подхватил ${intake.queued} готовых пакетов. ${intake.skipped} пропущены без отправки.`;
}

export function WorkContextProvider({ children }: { children: ReactNode }) {
  const { data, refreshWorkspace } = useApp();
  const location = useLocation();
  const calendar = useNativeCalendar();
  const calendarRoute = calendar.enabled && (/^\/calendar\/*$/u.test(location.pathname)
    || location.pathname==='/lead-magnets'||location.pathname.startsWith('/lead-magnets/'));
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [modePending, setModePending] = useState(false);
  const [modeError, setModeError] = useState<string | null>(null);
  const [modeNotice, setModeNotice] = useState<string | null>(null);
  const [contextNotice, setContextNotice] = useState<string | null>(null);
  const agents = useMemo(
    () => data.employees.filter((employee) => employee.status === "active"),
    [data.employees],
  );
  const requestedAgentId = params.get("agent") ?? "";
  const requestedScenarioId = params.get("scenario") ?? "";
  const resolution = useMemo(
    () => resolveWorkContext(
      agents,
      data.workflowScenarios,
      requestedAgentId,
      requestedScenarioId,
    ),
    [agents, data.workflowScenarios, requestedAgentId, requestedScenarioId],
  );
  const { activeScenarios, agentId, scenarioId } = resolution;
  const selectedScenario = scenarioId === ALL_ACTIVE_SCENARIOS
    ? null
    : activeScenarios.find((scenario) => scenario.id === scenarioId) ?? null;

  const commitContext = useCallback((key: "agent" | "scenario", value: string) => {
    setContextNotice(null);
    const next = new URLSearchParams(params);
    next.set(key, value);
    if (key === "agent") next.delete("scenario");
    next.delete("deal");
    next.delete("conversation");
    setParams(next, { replace: false });
  }, [params, setParams]);

  useEffect(() => {
    if (resolution.notice) setContextNotice(resolution.notice);
  }, [resolution.notice]);

  useEffect(() => {
    if (calendarRoute) return;
    if (!agentId) return;
    const currentAgent = params.get("agent");
    const currentScenario = params.get("scenario");
    if (currentAgent === agentId && currentScenario === scenarioId) return;
    const next = new URLSearchParams(params);
    next.set("agent", agentId);
    next.set("scenario", scenarioId);
    // The page's own #anchor stays (20.10.2026: «Как поставить на сайт» → /site-forms/<id>#install lost it here).
    navigate({ search: `?${next.toString()}`, hash: location.hash }, { replace: true });
  }, [agentId, params, scenarioId, navigate, location.hash, calendarRoute]);

  const setMode = useCallback(async (mode: "off" | "drafts" | "live") => {
    if (!selectedScenario?.activeActivation || modePending) return;
    const current = selectedScenario.operatingState;
    // Restore can preserve the scenario mode while disabling installation delivery.
    // Re-selecting that mode must reach the canonical server transition to resume it.
    const deliveryMatches = mode === "live"
      ? data.delivery.deliveryEnabled && data.delivery.autoDeliveryEnabled
      : mode === "drafts" ? data.delivery.deliveryEnabled : true;
    if (current.mode === mode && deliveryMatches) return;
    setModePending(true);
    setModeError(null);
    setModeNotice(null);
    try {
      const result = await updateScenarioOperatingMode(selectedScenario.id, {
        mode,
        expectedActivationId: selectedScenario.activeActivation.id,
        expectedScenarioVersionId: selectedScenario.activeActivation.scenarioVersionId,
        expectedRevision: current.revision,
        idempotencyKey: `scenario-mode-${crypto.randomUUID()}`,
      }, data.csrfToken);
      setModeNotice(liveReadyDraftIntakeNotice(result.readyDraftIntake));
      await refreshWorkspace();
    } catch (error) {
      setModeError(error instanceof Error ? error.message : "Не удалось изменить режим сценария.");
    } finally {
      setModePending(false);
    }
  }, [data.csrfToken, data.delivery.deliveryEnabled, data.delivery.autoDeliveryEnabled, modePending, refreshWorkspace, selectedScenario]);

  const contextSearch = useMemo(() => contextSearchOf(agents.length > 0, agentId, scenarioId, requestedAgentId, requestedScenarioId),
    [agentId, agents.length, requestedAgentId, requestedScenarioId, scenarioId]);

  const value = useMemo<WorkContextValue>(() => ({
    agentId,
    scenarioId,
    agents,
    activeScenarios,
    selectedScenario,
    setAgentId: (nextAgentId) => commitContext("agent", nextAgentId),
    setScenarioId: (nextScenarioId) => commitContext("scenario", nextScenarioId),
    setMode,
    modePending,
    modeError,
    modeNotice,
    contextNotice,
    contextSearch,
  }), [
    activeScenarios,
    agentId,
    agents,
    commitContext,
    contextSearch,
    contextNotice,
    modeError,
    modeNotice,
    modePending,
    scenarioId,
    selectedScenario,
    setMode,
  ]);

  return <WorkContext.Provider value={value}>{children}</WorkContext.Provider>;
}

export function useWorkContext(): WorkContextValue {
  const value = useContext(WorkContext);
  if (!value) throw new Error("useWorkContext must be used inside WorkContextProvider");
  return value;
}
